/**
 * Auth initialization hook
 *
 * Restores auth session on app startup by attempting token refresh.
 * The httpOnly refresh cookie (set by backend on login) is sent automatically.
 *
 * Flow:
 * 1. Wait for Zustand persist to rehydrate from localStorage
 * 2. If a valid (non-expired) token is in localStorage AND it belongs to the
 *    stored user, use it immediately, then refresh the profile from /me
 *    in the background so server-authoritative fields (e.g. is_platform_admin)
 *    aren't served stale from the login snapshot. If the token's subject names
 *    a different account than the stored profile (stale persisted identity),
 *    reconcile to the token via /me — the token is the source of truth
 *    for who the API acts as, so the displayed identity must follow it.
 * 3. Otherwise attempt /auth/refresh:
 *    - If there's a user in localStorage AND the refreshed token belongs to
 *      it, just update the access token
 *    - Otherwise (no user — cross-subdomain case: cookie visible but
 *      localStorage is empty because it's per-origin; OR the refreshed token
 *      is for a different account than the stored profile because the
 *      domain-wide refresh cookie was rewritten by a sibling-subdomain login)
 *      fetch /me to (re)populate the user store for the token
 * 4. Return loading state so route guards wait before redirecting
 *
 * Cross-subdomain note: On care-planner.staging.tricorder.icu, localStorage is empty
 * (per-origin) but the refresh cookie is visible because it's set with
 * Domain=staging.tricorder.icu (which covers all subdomains). This hook
 * detects that case via the empty-user branch and does 2 sequential calls
 * (refresh → /me) on first load. That's unavoidable without shared
 * storage across origins — do not "optimize" it away.
 *
 * Note: Must wait for rehydration because Safari is slower than Chrome/Brave.
 * Without waiting, accessToken appears null even when stored in localStorage.
 */

import { useEffect, useState } from "react";
import { useAuthStore } from "../stores/auth";
import { createLogger } from "@/utils/logger";
import { decodeJwtPayload, reconcileIdentity, tokenConflictsWithUser } from "@/lib/authIdentity";
import { refreshAdminTier } from "@/lib/adminTier";
import { fetchLaunchableAgents } from "@/hooks/useAgentCatalogue";
import { getApiBaseUrl } from "@/api/baseUrl";

const BASE_URL = getApiBaseUrl();
const logger = createLogger("AuthInit");

/** Check if a JWT is expired (with 30s safety margin for clock skew). */
function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload<{ exp?: number }>(token);
    if (!payload.exp) return true;
    return payload.exp * 1000 < Date.now() + 30_000;
  } catch {
    return true; // Malformed token → treat as expired
  }
}

interface AuthInitState {
  isInitializing: boolean;
  isAuthenticated: boolean;
}

export function useAuthInit(): AuthInitState {
  const [isInitializing, setIsInitializing] = useState(true);
  // After initialization completes, subscribe to store for login/logout updates
  const storeIsAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [initialAuthChecked, setInitialAuthChecked] = useState(false);

  useEffect(() => {
    async function initAuth() {
      // Wait for Zustand persist to rehydrate from localStorage
      // This is critical for Safari which rehydrates slower than Chrome/Brave
      await useAuthStore.persist.rehydrate();

      // The OAuth callback page's code exchange owns the session there: this
      // boot neither refreshes nor reconciles the OLD identity — both would
      // race the exchange (a refresh presents the pre-login cookie for a
      // guaranteed 401; a reconcile failure logs out mid-install).
      const onOAuthCallback = window.location.pathname.startsWith("/auth/callback");

      // Now read fresh state after rehydration
      const { user, accessToken, setAccessToken, setAuthReady, logout } =
        useAuthStore.getState();

      // If we have a stored token, check if it's expired before trusting it.
      // JWTs have base64url-encoded payload with an "exp" field (epoch seconds).
      if (user && accessToken && !isTokenExpired(accessToken)) {
        // A valid stored token is trusted only when it belongs to the stored
        // profile. If a sibling-subdomain login rewrote the domain-wide refresh
        // cookie and an earlier refresh swapped in that account's token, the
        // persisted user is stale — reconcile to the token (the real identity)
        // rather than display one nurse while the API acts as another.
        if (!tokenConflictsWithUser(accessToken, user)) {
          // Render immediately from the cached profile (fast path), then
          // refresh server-authoritative fields in the background. The stored
          // profile is a login-time snapshot that can predate fields the
          // backend derives per-request — notably is_platform_admin (the
          // OpenFGA admin tuple), carried only by /me. Without this an
          // admin who merely reloads keeps a stale isPlatformAdmin=false and
          // the admin UI stays hidden. Fire-and-forget: the token is valid, so
          // a transient /me failure must NOT drop the session — the 401
          // interceptor handles genuine revocation on the next request.
          setAuthReady();
          setInitialAuthChecked(true);
          setIsInitializing(false);
          void reconcileIdentity(accessToken, { onlyIfTokenStillActive: true });
          // The tier (/admin) and the launchable list (/agents) are lazy,
          // post-boot reads — neither blocks the session (care-app/ADR-0015).
          void refreshAdminTier();
          void fetchLaunchableAgents();
          return;
        }
        if (onOAuthCallback) {
          // A CONFLICTING stored identity on the callback page: reconciling
          // the old token here would either revert the exchange's setAuth
          // (a late reconcile re-bases the whole user) or log the session
          // out mid-install. The exchange owns the outcome; finish the boot
          // and let its continuation install tier and launchables.
          setInitialAuthChecked(true);
          setIsInitializing(false);
          return;
        }
        const reconciled = await reconcileIdentity(accessToken);
        if (!reconciled) logout();
        setInitialAuthChecked(true);
        setIsInitializing(false);
        void refreshAdminTier();
        void fetchLaunchableAgents();
        return;
      }

      // Token missing/expired or no user → attempt refresh via httpOnly cookie
      // This handles both:
      // 1. User in localStorage but expired token (original flow)
      // 2. No user in localStorage (cross-subdomain case — cookie exists but localStorage doesn't)
      //
      // EXCEPT on the OAuth callback page: the code exchange in flight there
      // installs the session itself (its setAuth sets authReady), and a
      // refresh fired now races its Set-Cookie with the browser's previous
      // cookie — a guaranteed 401 on every provider login, plus a store
      // teardown that can erase the session the exchange just installed
      // (#3173). The stored-token paths above still ran, so a signed-in user
      // landing back on a spent callback URL (browser Back, a second tab —
      // the no-exchange branch in OAuthCallback) with a VALID token keeps
      // the full fast path: authReady, the profile reconcile, the admin tier
      // and the launchable list. With an expired or absent stored session
      // nothing is restored on this route (the guard is pathname-based, not
      // exchange-based — a spent-URL boot with an expired token defers to a
      // reload); the exchange's outcome decides the next render.
      if (onOAuthCallback) {
        setInitialAuthChecked(true);
        setIsInitializing(false);
        return;
      }
      try {
        const response = await fetch(`${BASE_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          const data: unknown = await response.json();
          const newAccessToken =
            typeof data === "object" &&
            data !== null &&
            "access_token" in data &&
            typeof data.access_token === "string"
              ? data.access_token
              : "";

          if (!newAccessToken) {
            // Refresh returned 200 but response shape is wrong — treat as auth failure
            logger.error(
              "Refresh response missing access_token — clearing session",
            );
            if (user) logout();
            setInitialAuthChecked(true);
            setIsInitializing(false);
            return;
          }

          // Roll the token forward only when it belongs to the stored profile.
          // When there's no stored user (cross-subdomain first load: cookie
          // visible but per-origin localStorage empty) OR the refreshed token
          // is for a different account than the stored profile (domain-wide
          // refresh cookie rewritten by a sibling-subdomain login), fetch the
          // authoritative profile for the token so user+token always describe
          // the same account.
          if (user && !tokenConflictsWithUser(newAccessToken, user)) {
            setAccessToken(newAccessToken);
            setAuthReady();
            // Same background refresh as the fast path: a refreshed token can
            // outlive the login snapshot's server-authoritative fields (e.g.
            // is_platform_admin), so freshen them without blocking. Guarded so
            // a logout mid-flight can't resurrect the session.
            void reconcileIdentity(newAccessToken, {
              onlyIfTokenStillActive: true,
            });
          } else {
            const reconciled = await reconcileIdentity(newAccessToken);
            if (!reconciled) {
              // Refresh worked but /me failed → clear session
              logger.error(
                "Refresh succeeded but /me failed — clearing session",
              );
              logout();
            }
          }
        } else {
          // No refresh cookie or invalid → not authenticated
          // Only logout if we had a user (clears localStorage)
          if (user) {
            logout();
          }
        }
      } catch (err) {
        if (err instanceof TypeError) {
          // Network error (WiFi blip, train tunnel).
          // If we have a token (even expired), keep the session alive —
          // the 401 interceptor in client.ts will retry refresh on the next request.
          // authReady stays false → queries won't fire with the stale token.
          logger.warn(
            "Auth refresh network error — queries blocked until online",
            err,
          );
        } else {
          logger.error("Auth refresh unexpected error — clearing session", err);
          if (user) {
            logout();
          }
        }
      }

      setInitialAuthChecked(true);
      setIsInitializing(false);
    }

    void initAuth();
  }, []); // Run once on mount

  // During initialization, return false to prevent premature Safari redirects
  // After initialization, subscribe to store for login/logout/register reactivity
  const isAuthenticated = initialAuthChecked ? storeIsAuthenticated : false;

  return { isInitializing, isAuthenticated };
}
